import React, { useCallback } from 'react';
import classnames from 'classnames';
import { get, noop, isEmpty, castArray, defaultTo, times, isEqual, includes } from 'lodash';
import SimpleBar from 'simplebar-react';

// local dependencies
import { popover } from 'modules/popover';
import { Icon } from 'modules/icon-bank';
import { getLabel, isEmptyLabel } from 'utils/getLabel';
import { SORT_DIRECTIONS, GLOBAL_LABELS, WIDGET_ERROR_TYPE, ROW_VIEW_TEMPLATE_TYPE } from 'constants/common';
import { spinner as Spinner } from 'ui/views/generic/spinner';
import { EndScrollDetector } from 'modules/end-scroll-detector';
import { ChoiceList } from 'modules/choice-list';
import { progressLine as ProgressLine } from 'ui/views/generic/progressLine';
import { widgetError as WidgetError } from 'ui/views/widgets/widgetError';
import { checkbox as CheckBox } from 'ui/views/generic/checkbox';
import { isVisible, getVisibleItems } from 'resolvers/getVisibleEntities';
import { getMemberSearchSelectionKey } from 'utils/memberDetailSelection';

// row views
import { tableRow as TableRow } from './rowViews/defaultRow';
import { TableBlockRow } from './rowViews/tableBlockRow';


// sort button
const SortButton = ( props ) => {

    // arrow class names
    const upArrowClassName = classnames( 'ui-table__head__cell__sort__arrow ui-table__head__cell__sort__arrow--up', {
        'ui-table__head__cell__sort__arrow--active': SORT_DIRECTIONS.ASCENDING === props.value,
    } );
    const downArrowClassName = classnames( 'ui-table__head__cell__sort__arrow ui-table__head__cell__sort__arrow--down', {
        'ui-table__head__cell__sort__arrow--active': SORT_DIRECTIONS.DESCENDING === props.value,
    } );

    return (
        <div className='ui-table__head__cell__sort' onClick={ () => props.onChange( SORT_DIRECTIONS.ASCENDING === props.value ? SORT_DIRECTIONS.DESCENDING : SORT_DIRECTIONS.ASCENDING ) }>
            <div className={ upArrowClassName } />
            <div className={ downArrowClassName } />
        </div>
    );
};

// set display name
SortButton.displayName = 'table.SortButton';

/*-------*/

// filter button
const FilterButton = ( props ) => {

    // open popover options
    const openPopover = useCallback( ( element ) => {
        popover.open( {
            referenceElement: element,
            view: ChoiceList,
            tooltip: false,
            isDropdown: true,
            props: {
                width: props.flyoutWidth,
                allowFilter: true,
                displaySelectAllOption: ! get( props, 'config.searchOnline', false ),
                type: props.type,
                options: props.options,
                allowSearch: get( props, 'config.searchOnline', false ),
                config: get( props, 'config', {} ),
                handleQueryChange: ( search ) => props.fetchFilterSearchOptions( { config: props.config, search } ),
                placeholder: props.placeholder,
                selected: isEmpty( props.value ) ? [] : castArray( props.value ),
                onChange: props.onChange,
                dynamicSelectedOptions: props.dynamicSelectedOptions,
                choiceListClassName: 'ui-table__head__cell__filter__choice-list',
            }
        } );
    }, [ props.value ] );

    return (
        <div className='ui-table__head__cell__filter' onClick={ ( event ) => openPopover( event.currentTarget ) }>
            <Icon name='filter' className='ui-table__head__cell__filter__icon'/>
        </div>
    );
};

// set display name
FilterButton.displayName = 'table.FilterButton';

/****************************************/

// table selection Header
const TableSelection = ( props ) => {

    // check if selection action needs to be displayed on the selection head
    const selectionActions = getVisibleItems( get( props, 'actionsConfig.selectionActions.actions', [] ), props.visibility );

    // title of the selection along with count
    const title = `${ props.count } ${ getLabel( props.title ) }`;

    // show all checked or partially checked
    const checkVal = isEqual( props.count, props.items.length ) ? true : undefined;

    return (
        <div className='ui-table__selection-head' >
            <div className='ui-table__selection-head__icon'>
                <CheckBox size='xs' value={ checkVal } />
            </div>

            <div className='ui-table__selection-head__title'>
                { title }
            </div>

            { ! isEmpty( selectionActions ) ? (
                selectionActions.map( selectionAction => {

                    // getting icon from layout config
                    const iconName = get( props.layoutConfig, `actionIcons.${ selectionAction.name }`, 'click' );

                    return (
                        <div className='ui-table__selection-head__actions' key={ selectionAction.name }
                            onClick={ () => props.onActionClick( { type: 'selection', value: selectionAction, selection: props.selectedRows } ) }
                        >
                            <div className='ui-table__selection-head__actions__icon'>
                                <Icon
                                    name={ iconName }
                                    width={ 17 }
                                    height={ 17 }
                                    fill={ THEME.themeColorPrimary2 }
                                />
                            </div>

                            <div className='ui-table__selection-head__actions__title'>
                                { getLabel( selectionAction.title ) }
                            </div>
                        </div>
                    );
                } )
            ) : null }
        </div>
    );
};

// set display name
TableSelection.displayName = 'table.TableSelection';

/****************************************/

// table head (header) view
const TableHead = ( props ) => {

    // check if group action column needs to be displayed
    const isGroupActionsColumnVisible = isVisible( get( props, 'actionsConfig.groupActions', {} ), props.visibility );
    const groupActions = get( props, 'actionsConfig.groupActions.actions', [] );
    const groupActionsColumnName = get( props, 'actionsConfig.groupActions.columnTitle' );
    const groupActionsColumnWidth = get( props.layoutConfig, 'columnWidth._groupActions', 100 );

    // check if selection action column needs to be displayed
    const isSelectionActionsVisible = isVisible( get( props, 'actionsConfig.selectionActions', {} ), props.visibility );
    const selectionActions = get( props, 'actionsConfig.selectionActions', [] );
    const selectionActionsColumnName = get( props, 'actionsConfig.selectionActions.columnTitle' );
    const selectionActionsColumnWidth = get( props.layoutConfig, 'columnWidth._selectionActions', 100 );
    
    return (
        <div className='ui-table__head'>

            {/* progress line */}
            {
                props.isFetching ? (
                    <ProgressLine className='ui-table__head__progress-line'/>
                ) : null
            }

            {/* selection actions cells */}
            {
                ! isEmpty( selectionActions ) && isSelectionActionsVisible ? (
                    <div style={ { width: selectionActionsColumnWidth } } className='ui-table__head__select-cell'>
                        <div className='ui-table__head__select-cell__title'>
                            { ! isEmptyLabel( selectionActionsColumnName ) ? getLabel( selectionActionsColumnName ) : null }
                        </div>
                    </div>
                ) : null
            }

            {/* regular cells */}
            {
                props.columns.primary.map( column => {
                        
                    // get column width from the `layoutConfig` (default: 100px)
                    const columnWidth = get( props.layoutConfig, `columnWidth.${ column.name }`, 100 );

                    return (
                        <div key={ column.name } style={ { width: columnWidth } } className='ui-table__head__cell'>

                            {/* title */}
                            <div className='ui-table__head__cell__title'>
                                { getLabel( column.title ) }
                            </div>

                            {/* sort */}
                            {
                                get( column, 'sortAndFilterConfig.sort' ) ? (
                                    <SortButton
                                        value={ get( props.sortAndFilter, column.name ) }
                                        onChange={ ( value ) => props.onSortAndFilter( { type: 'sort', name: column.name, value } ) }
                                    />
                                ) : null
                            }

                            {/* filter */}
                            {
                                get( column, 'sortAndFilterConfig.filter' ) ? (
                                    <FilterButton
                                        value={ defaultTo( get( props.sortAndFilter, column.name ), [] ) }
                                        type={  column.type }
                                        options={ defaultTo( column.options, [] ) }
                                        config={ get( column, 'config' ) }
                                        placeholder={ `Search ${ getLabel( column.title ) }` }
                                        onChange={ ( value, list ) => props.onSortAndFilter( { type: 'filter', name: column.name, value, list  } ) }
                                        flyoutWidth={ get( props.layoutConfig, `filterFlyoutWidths.${ column.name }`, 150 ) }
                                        fetchFilterSearchOptions={ props.fetchFilterSearchOptions }
                                        dynamicSelectedOptions={ get( props, `dynamicSelectedOptions.${column.name }`, [] ) }
                                    />
                                ) : null
                            }
                        </div>
                    );
                } )
            }

            {/* group actions cells */}
            {
                ! isEmpty( groupActions ) && isGroupActionsColumnVisible ? (
                    <div style={ { width: groupActionsColumnWidth } } className='ui-table__head__cell'>
                        <div className='ui-table__head__cell__title'>
                            { ! isEmptyLabel( groupActionsColumnName ) ? getLabel( groupActionsColumnName ) : null }
                        </div>
                    </div>
                ) : null
            }
        </div>
    );
    
};

// set display name
TableHead.displayName = 'table.TableHead';

/********************************************/

// table - row view templates
const ROW_VIEW_TEMPLATES = {
    [ ROW_VIEW_TEMPLATE_TYPE.DEFAULT ]: TableRow,
    [ ROW_VIEW_TEMPLATE_TYPE.BLOCK ]: TableBlockRow,
};


/**
 * @type {object}
 * @desc A React presentation/dumb view component.
 * @example
 * import { table as TableView } from './table.view';
 *
 * // render
 * <TableView propName='propValue'/>
 */
export const table = ( props ) => {

    // get maximum table height from the `layoutConfig` (default: 300px)
    const maxTableHeight = get( props.layoutConfig, 'dimensions.maxHeight', 300 );

    // check if group action column needs to be displayed
    const groupActions = get( props, 'actionsConfig.groupActions.actions', [] );
    const groupActionsColumnWidth = get( props.layoutConfig, 'columnWidth._groupActions', 100 );

    // check if selection action column needs to be displayed
    const selectionActions = get( props, 'actionsConfig.selectionActions', [] );
    const sectionActionsTitle = get( props, 'actionsConfig.selectionActions.title' );

    // check if table selection view is disabled
    const disbleTableSelectionView = get( props, 'disbleTableSelectionView', false );

    // get the row view template
    const TableRowView = ROW_VIEW_TEMPLATES[ props.tableRowViewTemplate ];

    return (
        <div className='ui-table'>

            {/* table selection action*/}
            {
                
                ( ! disbleTableSelectionView && !isEmpty( selectionActions ) && props.selectedRows.length ) ? (
                    <TableSelection
                        key={ selectionActions.name }
                        title={ sectionActionsTitle }
                        count={  props.selectedRows.length }
                        { ...props }
                    />
                ) : null
                
            }
            
            {/* table head */}
            { ROW_VIEW_TEMPLATE_TYPE.DEFAULT === props.tableRowViewTemplate && (
                <TableHead { ...props } />
            ) }

            {/* table body */}
            {
                props.isFetching && 'pagination' !== props.fetchingType ? (

                    // table body: preloader view
                    <div className='ui-table__body-preloader'>
                        {
                            [ 1, 2, 3 ].map( index => {
                                return (
                                    <div key={ index } className='ui-table__body-preloader__row'>

                                        {/* regular cells */}
                                        {
                                            props.columns.primary.map( column => {

                                                // get column width from the `layoutConfig` (default: 100px)
                                                const columnWidth = get( props.layoutConfig, `columnWidth.${ column.name }`, 100 );

                                                return (
                                                    <div key={ column.name } style={ { width: columnWidth } } className='ui-table__body-preloader__row__cell'>
                                                        <div className='ui-table__body-preloader__row__cell__value' />
                                                        <div className='ui-table__body-preloader__row__cell__value' />
                                                    </div>
                                                );
                                            } )
                                        }

                                        {/* group action cell */}
                                        {
                                            ! isEmpty( groupActions ) ? (
                                                <div style={ { width: groupActionsColumnWidth } } className='ui-table__body-preloader__row__cell'>
                                                    <div className='ui-table__body-preloader__row__cell__value ui-table__body-preloader__row__cell__value--icon' />
                                                    <div className='ui-table__body-preloader__row__cell__value' />
                                                </div>
                                            ) : null
                                        }
                                    </div>
                                );
                            } )
                        }
                    </div>
                ) : (

                    // table body: data view
                    <div className='ui-table__body'>
                        {
                            isEmpty( props.items ) ? (
                                <div className='ui-table__body__no-data'>
                                    <WidgetError type={ props.isInitialData ? WIDGET_ERROR_TYPE.NO_DATA_FOUND : WIDGET_ERROR_TYPE.NO_MATCH_FOUND }/>
                                </div>
                            ) : (
                                <EndScrollDetector
                                    disabled={ props.disablePagination }
                                    callback={ props.onPagination }
                                    render={ ( { ref } ) => (
                                        <SimpleBar scrollableNodeProps={ { ref } } style={ { maxHeight: maxTableHeight } }>
                                            <div className='ui-table__body__data'>
                                                {
                                                    props.items.map( ( item, rowIndex ) => {
                                                        return (
                                                            <TableRowView
                                                                key={ item.__memberSearchRowKey || item.id }
                                                                rowIndex={ rowIndex }
                                                                item={ item }
                                                                { ...props }
                                                                isLiveFallback={ typeof item.__liveMemberSearch === 'boolean'
                                                                    ? item.__liveMemberSearch
                                                                    : includes( props.liveFallbackKeys, getMemberSearchSelectionKey( item ) ) }
                                                            />
                                                        );
                                                    } )
                                                }
                                            </div>
                                        </SimpleBar>
                                    ) }
                                />
                            )
                        }
                    </div>
                )
            }

            {/* pagination spinner */}
            {
                props.isFetching && 'pagination' === props.fetchingType ? (
                    <div className='ui-table__pagination-spinner'>
                        <Spinner />
                    </div>
                ) : null
            }
        </div>
    );
};

// set display name
table.displayName = 'table';

// set default props
table.defaultProps = {
    isFetching: false,
    fetchingType: null,
    disablePagination: false,
    disbleTableSelectionView: false,
    tableRowViewTemplate: ROW_VIEW_TEMPLATE_TYPE.DEFAULT,

    // static data
    layoutConfig: {},
    searchableColumns: [],
    actionsConfig: {},

    // data
    searchQuery: '',
    items: [],
    columns: {
        primary: [],
        secondary: [],
    },
    sortAndFilter: {},
    pagination: {},
    noDataLabel: GLOBAL_LABELS.DISPLAY_TEXT_NO_DATA_FOUND,

    // methods
    onSortAndFilter: noop,
    onPagination: noop,
    onActionClick: noop,
    fetchFilterSearchOptions: noop,
    additionalTableInfo: {},
};

// prop types
table.propTypes = {};

/********************************************/

/**
 * @type {object}
 * @desc A React presentation/dumb view component.
 * @example
 * import { tablePreloader as TablePreloader } from './table.view';
 *
 * // render
 * <TablePreloader propName='propValue'/>
 */
export const tablePreloader = () => {
    return (
        <div className='ui-table-preloader'>
            <div className='ui-table-preloader__head'>
                {
                    times( 9 ).map( colIndex => {
                        return (
                            <div key={ colIndex } className='ui-table-preloader__head__cell'>
                                <div className='ui-table-preloader__head__cell__value' />
                            </div>
                        );
                    } )
                }
            </div>

            <div className='ui-table-preloader__body'>
                {
                    times( 5 ).map( rowIndex => {
                        return (
                            <div key={ rowIndex } className='ui-table-preloader__body__row'>
                                {
                                    times( 9 ).map( colIndex => {
                                        return (
                                            <div key={ colIndex } className='ui-table-preloader__body__row__cell'>
                                                <div className='ui-table-preloader__body__row__cell__value' />
                                                <div className='ui-table-preloader__body__row__cell__value' />
                                            </div>
                                        );
                                    } )
                                }
                            </div>
                        );
                    } )
                }
            </div>
        </div>
    );
};

// set display name
table.tablePreloader = 'tablePreloader';

// set default props
table.tablePreloader = {};

// prop types
table.tablePreloader = {};
